import React, { useState, useEffect } from 'react'
import {
  Activity, Search, Filter, RefreshCw, Eye, Calendar,
  User, Shield, LogIn, LogOut, Edit, Trash2, Clock,
  Download, AlertCircle
} from 'lucide-react'
import { supabase } from '../../services/supabase'
import { formatDate } from '../../utils/formatters'
import toast from 'react-hot-toast'

const ActivityLogs = () => {
  const [logs, setLogs] = useState([])
  const [filteredLogs, setFilteredLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [actionFilter, setActionFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')
  const [selectedLog, setSelectedLog] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)

  useEffect(() => {
    loadLogs()

    // Real-time subscription
    const subscription = supabase
      .channel('activity_logs_changes')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'activity_logs' },
        (payload) => {
          console.log('New activity log:', payload)
          setLogs(prev => [payload.new, ...prev])
          toast.success('New activity detected!')
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    filterLogs()
  }, [logs, searchTerm, actionFilter, dateFilter])

 const loadLogs = async () => {
  setLoading(true)
  try {
    // First get activity logs
    const { data: logsData, error: logsError } = await supabase
      .from('activity_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)

    if (logsError) throw logsError

    // Then get user info for each log
    if (logsData && logsData.length > 0) {
      const userIds = [...new Set(logsData.map(l => l.user_id).filter(Boolean))]
      
      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, username, email, full_name, name')
          .in('id', userIds)

        if (usersError) throw usersError

        // Merge user data with logs
        const logsWithUsers = logsData.map(log => ({
          ...log,
          users: usersData?.find(u => u.id === log.user_id) || null
        }))

        setLogs(logsWithUsers)
      } else {
        setLogs(logsData)
      }
    } else {
      setLogs([])
    }
  } catch (error) {
    console.error('Error loading activity logs:', error)
    toast.error('Failed to load activity logs')
  } finally {
    setLoading(false)
  }
}

  const filterLogs = () => {
    let filtered = [...logs]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.users?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.users?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.ip_address?.includes(searchTerm)
      )
    }

    // Action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter)
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date()
      const filterDate = new Date()

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0)
          break
        case 'week':
          filterDate.setDate(now.getDate() - 7)
          break
        case 'month':
          filterDate.setMonth(now.getMonth() - 1)
          break
      }

      filtered = filtered.filter(log => new Date(log.created_at) >= filterDate)
    }

    setFilteredLogs(filtered)
  }

  const handleViewDetails = (log) => {
    setSelectedLog(log)
    setShowDetailsModal(true)
  }

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'User', 'Action', 'Details', 'IP Address'],
      ...filteredLogs.map(log => [
        formatDate(log.created_at),
        log.users?.email || 'System',
        log.action,
        log.details || '',
        log.ip_address || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    toast.success('Activity logs exported!')
  }

  const getActionIcon = (action) => {
    const icons = {
      login: <LogIn className="w-4 h-4 text-green-600" />,
      logout: <LogOut className="w-4 h-4 text-gray-600" />,
      user_created: <User className="w-4 h-4 text-blue-600" />,
      user_updated: <Edit className="w-4 h-4 text-yellow-600" />,
      user_deleted: <Trash2 className="w-4 h-4 text-red-600" />,
      kyc_approved: <Shield className="w-4 h-4 text-green-600" />,
      kyc_rejected: <Shield className="w-4 h-4 text-red-600" />,
      transaction_created: <Activity className="w-4 h-4 text-purple-600" />,
      login_as_user: <LogIn className="w-4 h-4 text-orange-600" />
    }
    return icons[action] || <Activity className="w-4 h-4 text-gray-600" />
  }

  const getActionBadge = (action) => {
    const badges = {
      login: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      logout: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400',
      user_created: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      user_updated: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      user_deleted: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      kyc_approved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      kyc_rejected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      transaction_created: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
    }
    return badges[action] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
  }

  // Get unique actions for filter
  const uniqueActions = [...new Set(logs.map(log => log.action))].filter(Boolean)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Activity Logs
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {filteredLogs.length} of {logs.length} logs • Real-time monitoring
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={loadLogs}
            className="px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
            />
          </div>

          {/* Action Filter */}
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
          >
            <option value="all">All Actions</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>
                {action.replace(/_/g, ' ').toUpperCase()}
              </option>
            ))}
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-dark-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  Details
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  IP Address
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No activity logs found
                  </td>
                </tr>
              ) : (
                filteredLogs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {formatDate(log.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {log.users?.full_name || log.users?.name || log.users?.username || 'System'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {log.users?.email || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <span className={`px-2 py-1 text-xs rounded-full inline-flex items-center gap-1 ${getActionBadge(log.action)}`}>
                        {getActionIcon(log.action)}
                        {log.action?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <p className="text-sm text-gray-900 dark:text-white truncate max-w-xs">
                        {log.details || 'No details'}
                      </p>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                      {log.ip_address || 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={() => handleViewDetails(log)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Log Details Modal */}
      {showDetailsModal && selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                Activity Log Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Log ID */}
              <div className="pb-4 border-b border-gray-200 dark:border-dark-700">
                <label className="text-xs text-gray-500 dark:text-gray-400">Log ID</label>
                <p className="text-sm font-mono text-gray-900 dark:text-white break-all mt-1">
                  {selectedLog.id}
                </p>
              </div>

              {/* Timestamp */}
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Timestamp</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                  {formatDate(selectedLog.created_at)}
                </p>
              </div>

              {/* User Info */}
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">User</label>
                <div className="flex items-center gap-3 mt-2 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedLog.users?.full_name || selectedLog.users?.name || selectedLog.users?.username || 'System'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedLog.users?.email || 'System action'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action */}
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Action</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                  <span className={`px-3 py-1 text-sm rounded-full inline-flex items-center gap-2 ${getActionBadge(selectedLog.action)}`}>
                    {getActionIcon(selectedLog.action)}
                    {selectedLog.action?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </p>
              </div>

              {/* Details */}
              {selectedLog.details && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Details</label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                    {selectedLog.details}
                  </p>
                </div>
              )}

              {/* IP Address & User Agent */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">IP Address</label>
                  <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">
                    {selectedLog.ip_address || 'Not recorded'}
                  </p>
                </div>
                {selectedLog.user_agent && (
                  <div className="sm:col-span-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400">User Agent</label>
                    <p className="text-xs text-gray-900 dark:text-white mt-1 p-2 bg-gray-50 dark:bg-dark-700 rounded break-all">
                      {selectedLog.user_agent}
                    </p>
                  </div>
                )}
              </div>

              {/* Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Additional Metadata</label>
                  <pre className="text-xs text-gray-900 dark:text-white mt-1 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg overflow-auto max-h-48">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-dark-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ActivityLogs